import React, { FormEvent, useCallback, useEffect, useState } from 'react'

import useUpdateProgram from '../../../hooks/program/useUpdateProgram';
import useCreateProgram from '../../../hooks/program/useCreateProgram';
import useDeleteProgram from '../../../hooks/program/useDeleteProgram';
import useGetTags from '../../../hooks/tags/useGetTags';
import useDuplicateProgram from '../../../hooks/program/useDuplicateProgram';
import useGetProvinces from '../../../hooks/provinces/useGetProvinces';

import { IProgram, ProgramMethod } from '../../../types/program';
import { ISaveTag, ITag } from '../../../types/tags';

import { SVGIcon } from '../../wrappers/SVGIcon';

import { Icons } from '../../../data/Assets';


interface IProgramProps {
  program: IProgram;
  currOption: number[];
  setCurrOption: React.Dispatch<React.SetStateAction<number[]>>;
  isDelete: boolean;
  isCreateDuplicate: boolean;
  setIsDelete: React.Dispatch<React.SetStateAction<boolean>>;
  setIsOpenDeleteModal: React.Dispatch<React.SetStateAction<boolean>>;
  setIsResetNew: React.Dispatch<React.SetStateAction<boolean>>;
  setDeleteModalMessage: React.Dispatch<React.SetStateAction<{ title: string; subtitle: string; }>>;
  setDuplicateModalText: React.Dispatch<React.SetStateAction<{ title: string; subtitle: string; }>>;
  setCreateDuplicate: React.Dispatch<React.SetStateAction<boolean>>;
  setCreateDuplicateModal: React.Dispatch<React.SetStateAction<boolean>>;
  setIsTagsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

const Program = ({ program, currOption, setCurrOption, isDelete, setIsDelete, setIsOpenDeleteModal, setIsResetNew, setDeleteModalMessage, setCreateDuplicate, setCreateDuplicateModal, setDuplicateModalText, isCreateDuplicate, setIsTagsModalOpen }: IProgramProps) => {
  const [title, setTitle] = useState<string>(program.id ? program.name : 'New Program');

  const [currentProgram, setCurrentProgram] = useState<IProgram>(program);
  const [isProcessRunning, setIsProcessRunning] = useState<boolean>(false);
  const [apiError, setApiError] = useState<string | null>(null); //eslint-disable-line
  const [isEditLink, setIsEditLink] = useState<boolean>(false);
  const [showTags, setShowTags] = useState<boolean>(false);
  const [tags, setTags] = useState<ITag[]>([]);
  const [tagSearch, setTagSearch] = useState<string>('');
  const [selectedTags, setSelectedTags] = useState<ISaveTag[]>([]);

  const { data: provinces } = useGetProvinces();

  useEffect(() => {
    if (!program?.gov_url || program?.gov_url?.length === 0) {
      setIsEditLink(true);
    } else {
      setIsEditLink(false);
    }

    if (program.program_tags) {
      const progTags = program.program_tags.flatMap(progTag => progTag.tag);

      setSelectedTags(progTags.map(tag => ({ id: tag.id, name: tag.name })));
    }

    setCurrentProgram(program);

    setTitle(program.id ? program.name : 'New Program');
  }, [program]);

  const { data: tagsData } = useGetTags(true);

  const { mutate: updateProgram } = useUpdateProgram({ setIsProcessRunning, setIsServiceError: setApiError });
  const { mutate: createProgram } = useCreateProgram({ setIsProcessRunning, setIsServiceError: setApiError });
  const { mutate: deleteProgram } = useDeleteProgram();
  const { mutate: duplicateProgram } = useDuplicateProgram();

  useEffect(() => {
    if (tagsData?.data) {
      setTags(tagsData.data);
    }
  }, [tagsData]); // eslint-disable-line

  useEffect(() => {
    if (isDelete) {
      if (currOption.length - 1 === 1) {
        setCurrOption([currOption[0]]);
      } else {
        setCurrOption([currOption[0], 0]);
      }
      if (!program.id) return;
      deleteProgram(program.id);
      setIsDelete(false);
    }
  }, [isDelete]); //eslint-disable-line

  useEffect(() => {
    if (!program.id) return;
    if (isCreateDuplicate && program?.id) {
      duplicateProgram({
        program_id: program.id,
        setIsProcessing: setIsProcessRunning,
        setApiError: setApiError,
        successCallback: () => {
          setCurrOption([currOption[0], currOption[1]]);
          setCreateDuplicate(false)
        }
      });
    }
  }, [program?.id, isCreateDuplicate]) //eslint-disable-line

  const getDisplayTags = useCallback(() => {
    let tagsToDisplay = tags;
    if (selectedTags.length) {
      tagsToDisplay = tagsToDisplay.filter(tag => !selectedTags.find(selectedTag => selectedTag.id === tag.id));
    }

    if (tagSearch.length) {
      tagsToDisplay = tagsToDisplay.filter(tag => tag.name.toLowerCase().includes(tagSearch.toLowerCase()));
    }

    return tagsToDisplay;
  }, [tags, selectedTags, tagSearch]);

  if (!currentProgram) {
    return <p>Loading...</p>
  }

  const handleSave = (e: FormEvent) => {
    e.preventDefault();

    if (!program.category_id) return;

    const saveProgramData = {
      id: program.id,
      category_id: program.category_id,
      active: currentProgram.active,
      name: currentProgram.name,
      description: currentProgram.description,
      method: currentProgram.method,
      gov_url: currentProgram.gov_url,
      province_id: currentProgram.province_id,
      tags: selectedTags
    };

    if (!program.id) {
      createProgram(saveProgramData);
    } else {
      updateProgram(saveProgramData);
    };
    setTitle(currentProgram.name || 'New Program');
  }

  const handleDelete = () => {
    if (!program.id) {
      setIsResetNew(true);
      if (currOption.length - 1 === 1) {
        setCurrOption([currOption[0]]);
      } else {
        setCurrOption([currOption[0], 0]);
      }
    } else {
      setIsOpenDeleteModal(true);
      setDeleteModalMessage({ title: 'Delete Program', subtitle: 'This will delete all the components, functions and parameters associated with the program. Are you sure you want to delete?' });
    }
  }

  const onSelectTag = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, tag: ISaveTag) => {
    e.preventDefault();

    setSelectedTags([...selectedTags, { id: tag.id ?? null, name: tag.name }]);
    setTagSearch('');
  }

  const openDuplicateModal = () => {
    setDuplicateModalText({ title: 'Create Duplicate', subtitle: 'This will create a duplicate of the program. Are you sure you want to continue?' });
    setCreateDuplicateModal(true);
  };

  return (
    <div className='imm_admin__content'>
      <div className='content_head'>
        <h3>Program - {title}</h3>
        {program.id && <button type='button' onClick={openDuplicateModal}>Create Duplicate</button>}
      </div>
      {apiError && <span className='color_error'>{apiError}</span>}

      <form onSubmit={handleSave} className='content_form'>
        <div className='field'>
          <label htmlFor='program'>Program <span className='field__required'>*</span></label>
          <input id='program' name='program' placeholder='Enter Program' value={currentProgram.name || ''} onChange={(e) => setCurrentProgram({ ...currentProgram, name: e.target.value })} />
        </div>

        <div className='field'>
          <label htmlFor='description'>Description <span className='field__required'>*</span></label>
          <textarea id='description' name='description' placeholder='Enter Description' value={currentProgram.description || ""} onChange={(e) => setCurrentProgram({ ...currentProgram, description: e.target.value })}></textarea>
        </div>

        {program.id ? (
          <div className='field'>
            <label htmlFor='status'>Status</label>
            <select id='status' defaultValue={-1} name='status' value={currentProgram.active ? 1 : 0} onChange={(e) => { setCurrentProgram({ ...currentProgram, active: !!Number(e.target.value) }) }}>
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
            </select>
          </div>

        ) : null}

        <div className='field'>
          <label htmlFor='method'>Method <span className='field__required'>*</span></label>
          <select id='method' name='method' value={currentProgram.method || "and"} onChange={(e) => setCurrentProgram({ ...currentProgram, method: e.target.value as ProgramMethod })}>
            <option value={ProgramMethod.AND}>and</option>
            <option value={ProgramMethod.OR}>or</option>
            {/* <option value={ProgramMethod.NOT}>not</option>
            <option value={ProgramMethod.NAND}>nand</option>
            <option value={ProgramMethod.NOR}>nor</option>
            <option value={ProgramMethod.XOR}>xor</option> */}
          </select>
        </div>

        <div className='field'>
          <label htmlFor='gov_url'>Action Link <span className='field__required'>*</span>
            <button type='button' onClick={() => setIsEditLink(true)}>
              <SVGIcon src={Icons.Edit} />
            </button>
          </label>
          {isEditLink && <div className='action__links'><input id='gov_url' name='gov_url' placeholder='Enter Government URL' value={currentProgram.gov_url || ''} onChange={(e) => setCurrentProgram({ ...currentProgram, gov_url: e.target.value })} /><button type='button' onClick={() => setIsEditLink(false)}>Done</button></div>}
          {!isEditLink && <a className='action__links' href={!currentProgram?.gov_url?.includes('http') ? `https://${currentProgram.gov_url}` : currentProgram.gov_url} target='_blank' rel="noreferrer" >{currentProgram.gov_url}</a>}
        </div>

        <div className='field'>
          <label htmlFor='method'>Province</label>
          <select id='province' name='province' value={currentProgram.province_id ?? ''} onChange={(e) => setCurrentProgram({ ...currentProgram, province_id: isNaN(Number(e.target.value)) ? null : Number(e.target.value) })}>
            <option value={''}>-- Select Province --</option>
            {provinces?.map(province => (
              <option key={province.id} value={province.id}>{province.name}</option>
            ))}
          </select>
        </div>

        <div className='field'>
          <label htmlFor='tags'>
            <span>Tags</span>
            <button type="button" onClick={() => setIsTagsModalOpen(true)}><SVGIcon src={Icons.Edit} /></button>
          </label>

          {selectedTags.length > 0 && <div className='selected_tags'>
            {selectedTags.map((tag, index) => (
              <div key={index} className='tag_badge'>
                <span>{tag.name}</span>
                <button type='button' onClick={() => setSelectedTags(selectedTags.filter(selectedTag => selectedTag.id !== tag.id))}><SVGIcon src={Icons.X} /></button>
              </div>
            ))}
          </div>}
          <input
            id='tags'
            name='tags'
            placeholder='Select or create a tag'
            onFocus={() => setShowTags(true)}
            onBlur={() => setShowTags(false)}
            value={tagSearch || ''}
            onChange={(e) => setTagSearch(e.target.value)} />
          {/* display tags list only if tagRef is focused */}

          {showTags && <div className='tags_list'>
            {getDisplayTags().map((tag, index) => (
              <div key={index} className='tag_options'>
                <button
                  type="button"
                  onMouseDown={(e) => onSelectTag(e, tag)}
                >
                  {tag.name}
                </button>
              </div>
            ))}

            {(tagSearch.length > 0 && getDisplayTags().length === 0) && <div className='tag_options'>
              <button
                type='button'
                onMouseDown={(e) => onSelectTag(e, { id: null, name: tagSearch })}
              >
                <SVGIcon src={Icons.Plus} /> Create New
              </button>
            </div>}
          </div>}
        </div>

        <div className='actions'>
          <button className='delete_btn' type="button" onClick={handleDelete}>{!program.id ? 'Cancel' : 'Delete'}</button>
          <button className='save_btn' type='submit'>{isProcessRunning ? <i className='imm_admin__process_loader' /> : !program.id ? 'Create' : 'Save'}</button>
        </div>
      </form>
    </div>
  );
}

export default Program;